import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import cn from 'classnames';
import { mergeProps } from '@react-aria/utils';
import { SlotContext, useSlot } from '../slots';
import { Typography } from '../typography';
import { useTheme } from '../ThemeProvider';
import styles from './styles.module.css';
const LABEL_DEFAULTS = {
    variant: 'caption1',
    group: 'tight',
    thickness: 'regular',
    color: 'minor',
};
const HELPER_TEXT_DEFAULTS = LABEL_DEFAULTS;
const INPUT_DEFAULTS = {
    variant: 'body2',
    group: 'tight',
    thickness: 'regular',
    color: 'main',
};
function useLabel({ inputProps, labelProps, focused, value, placeholder, name, animated, }) {
    const theme = useTheme();
    const variables = useMemo(() => {
        const { variant: inputVar, group: inputGr, thickness: inputTh, } = { ...INPUT_DEFAULTS, ...inputProps };
        const { variant: labelVar, group: labelGr } = { ...LABEL_DEFAULTS, ...labelProps };
        // во сколько раз шрифт инпута отличается от шрифта лейбла
        const labelScale = theme.fonts.sizes[inputVar] / theme.fonts.sizes[labelVar];
        const labelHeight = theme.fonts.heights[labelVar][labelGr];
        // во сколько раз высота инпута отличается от высоты лейбла после скейла
        const heightDiff = theme.fonts.heights[inputVar][inputGr] / (labelHeight * labelScale);
        // деление на labelScale приводит к системе координат до скейла
        // умножение на 0.5 центрирует лейбл на уровне плейсхолдера
        // умножение на heightDiff выравнивает текст
        const labelTranslate = `${(labelHeight * heightDiff * 0.5) / labelScale}px`;
        // подгоняем лейбл под шрифт инпута
        return {
            ['--labelScale']: labelScale,
            ['--labelTranslate']: labelTranslate,
            ['--labelSpacing']: theme.fonts.spacings[inputVar][inputTh],
        };
    }, [labelProps, inputProps, theme]);
    const labelFinalProps = useMemo(() => {
        return {
            ...LABEL_DEFAULTS,
            ...labelProps,
            style: variables,
            htmlFor: name,
            Component: 'label',
            className: cn(styles.label, {
                [styles.transformed]: !focused && !value && !placeholder && animated !== false,
            }),
        };
    }, [labelProps, variables, name, value, placeholder, focused]);
    return labelFinalProps;
}
function useInput({ onChange, inputProps, name, value, placeholder, error, focused, disabled, readOnly, multiline, autoComplete = 'off', }, inputRef) {
    const onInputChange = useMemo(() => {
        if (!onChange)
            return undefined;
        return function onChangeInner(e) {
            return onChange(e.currentTarget.value, e);
        };
    }, [onChange]);
    const inputFinalProps = useMemo(() => {
        const mergedProps = {
            ...INPUT_DEFAULTS,
            ...inputProps,
        };
        return {
            ...mergedProps,
            color: error && !focused ? 'error' : mergedProps.color,
            readOnly: disabled || readOnly,
            tabIndex: disabled || readOnly ? -1 : undefined,
            id: name,
            value,
            placeholder,
            onChange: onInputChange,
            multiline,
            autoComplete,
            ref: inputRef,
        };
    }, [inputProps, name, value, placeholder, onInputChange, error, focused, disabled]);
    return inputFinalProps;
}
export function useTextField(props) {
    const { size = 'm', variant = 'line', focusOnClick, inputProps } = props;
    const context = useMemo(() => ({ size, noTrailingPadding: variant === 'line' }), [size, variant]);
    const { heightClass, sizeClass } = useSlot({ size });
    const inputRef = useRef(null);
    const onMouseDown = useCallback((e) => {
        var _a;
        if (focusOnClick === false || e.target === inputRef.current)
            return;
        // превентим блюр
        e.preventDefault();
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [focusOnClick]);
    useImperativeHandle(inputProps === null || inputProps === void 0 ? void 0 : inputProps.ref, () => inputRef.current);
    return {
        className: cn(sizeClass, heightClass),
        context,
        variant,
        size,
        labelProps: useLabel(props),
        inputProps: useInput(props, inputRef),
        onMouseDown,
    };
}
export const TextFieldBase = forwardRef((props, ref) => {
    const { value, className, labelProps: _, inputProps: __, helperTextProps, children, focused, onChange, placeholder, name, error, errorMessage, description, disabled, multiline, animated, focusOnClick, ...rest } = props;
    const { context, labelProps, inputProps, variant, size, className: textBodyClassName, ...restFieldProps } = useTextField(props);
    const text = error ? errorMessage || description : description;
    return (React.createElement(SlotContext.Provider, { value: context },
        React.createElement("div", { ref: ref, className: cn(styles.textField, styles[variant], styles[size], className, {
                [styles.focus]: focused,
                [styles.error]: error,
                [styles.disabled]: disabled,
                [styles.multiline]: multiline,
            }), ...mergeProps(restFieldProps, rest) },
            React.createElement("div", { className: cn(textBodyClassName, styles.textFieldBody) }, children({
                labelProps,
                inputProps,
                error,
            })),
            text && (React.createElement(Typography, { className: cn(styles.helperText), ...HELPER_TEXT_DEFAULTS, ...helperTextProps, color: error ? 'error' : 'main' }, text)))));
});
export const TextField = forwardRef(({ onFocus, onBlur, ...rest }, ref) => {
    const [isFocused, setFocused] = useState(false);
    const onFocusHandler = useCallback((e => {
        setFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }), [onFocus]);
    const onBlurHandler = useCallback((e => {
        setFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }), [onBlur]);
    return (React.createElement(TextFieldBase, { onFocus: onFocusHandler, onBlur: onBlurHandler, focused: isFocused, ...rest, ref: ref }));
});
